import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import { Box, Modal, TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as React from "react";

import { useHttpClient } from "@/providers/HttpClient";
import { RobotoButton } from "@/shared/components/RobotoButton";
import { RobotoIconButton } from "@/shared/components/RobotoIconButton";
import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import styles from "./ChatFeedback.module.css";

interface ChatFeedbackProps {
  chatId: string;
  messageIndex: number;
  lastAssistantText?: string;
}

export const ChatFeedback: React.FC<ChatFeedbackProps> = ({
  chatId,
  messageIndex,
  lastAssistantText,
}) => {
  const theme = useTheme();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [feedbackType, setFeedbackType] = React.useState<
    "positive" | "negative" | null
  >(null);
  const [message, setMessage] = React.useState("");
  const { httpClient } = useHttpClient();

  const handleFeedback = (type: "positive" | "negative") => {
    setFeedbackType(type);
    setModalOpen(true);
  };

  const sendFeedback = async () => {
    try {
      await httpClient.post("/v1/feedback", {
        body: JSON.stringify({
          message: JSON.stringify({
            feature: "ai_chat",
            chat_id: chatId,
            message_index: messageIndex,
            lastMessageContent: lastAssistantText,
            feedbackType,
            message,
          }),
        }),
      });
    } catch (error) {
      ErrorMonitoringService.captureError(error, {
        context: { chatId, messageIndex, feedbackType },
      });
    }

    setModalOpen(false);
    setMessage("");
    setFeedbackType(null);
  };

  const context = { chatId, messageIndex };

  return (
    <>
      <Box className={styles.feedbackContainer}>
        <RobotoTooltip title="Good response">
          <span>
            <RobotoIconButton
              eventName="ChatFeedbackPositiveClicked"
              eventProperties={context}
              onClick={() => void handleFeedback("positive")}
              className={styles.iconButton}
              sx={{
                "&:hover": {
                  "& .MuiSvgIcon-root": {
                    color: theme.palette.success.main,
                  },
                },
              }}
            >
              <ThumbUpAltOutlinedIcon className={styles.thumbIcon} />
            </RobotoIconButton>
          </span>
        </RobotoTooltip>

        <RobotoTooltip title="Bad response">
          <span>
            <RobotoIconButton
              eventName="ChatFeedbackNegativeClicked"
              eventProperties={context}
              onClick={() => void handleFeedback("negative")}
              className={styles.iconButton}
              sx={{
                "&:hover": {
                  "& .MuiSvgIcon-root": {
                    color: theme.palette.error.main,
                  },
                },
              }}
            >
              <ThumbDownAltOutlinedIcon className={styles.thumbIcon} />
            </RobotoIconButton>
          </span>
        </RobotoTooltip>
      </Box>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box
          className={styles.modalContent}
          sx={{
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[24],
          }}
        >
          <RobotoTypography
            variant="h6"
            className={styles.modalTitle}
            sx={{ color: theme.palette.text.primary }}
          >
            {feedbackType === "positive"
              ? "What did you like?"
              : "What could be improved?"}
          </RobotoTypography>
          <TextField
            placeholder="Optional feedback message"
            multiline
            rows={4}
            fullWidth
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className={styles.feedbackInput}
          />
          <Box className={styles.buttonContainer}>
            <RobotoButton
              eventName="ChatFeedbackCancelClicked"
              eventProperties={context}
              variant="text"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </RobotoButton>
            <RobotoButton
              eventName="ChatFeedbackSubmitClicked"
              eventProperties={{ ...context, message, feedbackType }}
              variant="contained"
              onClick={() => void sendFeedback()}
            >
              Submit
            </RobotoButton>
          </Box>
        </Box>
      </Modal>
    </>
  );
};
